import { Button } from '@/components/ui/button'
import { ArrowRight, CalendarCheck, IdentificationCard, Package, Sparkle } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/hooks/useAuth'

type ProfessionalWelcomeStepProps = {
  tenantName: string | null
  onNext: () => void
  onSkip: () => void
}

export function ProfessionalWelcomeStep({ tenantName, onNext, onSkip }: ProfessionalWelcomeStepProps) {
  const { t } = useTranslation('onboarding')
  const { user } = useAuth()

  const features = [
    { icon: IdentificationCard, label: t('professional.welcome.features.profile') },
    { icon: Package, label: t('professional.welcome.features.services') },
    { icon: CalendarCheck, label: t('professional.welcome.features.availability') },
  ]

  return (
    <div className="flex flex-col items-center px-4 py-6 text-center animate-in fade-in duration-500">
      <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-[#F08A24] to-[#f9a84c] mb-4">
        <Sparkle weight="bold" className="w-7 h-7 text-white" />
      </div>

      <h2 className="text-2xl font-bold text-[#0B2A4A] dark:text-white mb-2">
        {user?.name
          ? t('professional.welcome.titleWithName', { name: user.name.split(' ')[0] })
          : t('professional.welcome.title')}
      </h2>
      <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto mb-2">
        {tenantName
          ? t('professional.welcome.subtitleWithTenant', { tenant: tenantName })
          : t('professional.welcome.subtitle')}
      </p>
      <p className="text-sm text-gray-400 dark:text-gray-500 max-w-md mx-auto mb-8">
        {t('professional.welcome.description')}
      </p>

      <div className="grid gap-3 w-full max-w-md mb-10">
        {features.map(({ icon: Icon, label }) => (
          <div
            key={label}
            className="flex items-center gap-3 rounded-xl border border-gray-200 dark:border-gray-700 p-4 text-left"
          >
            <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-[#F08A24]/10 shrink-0">
              <Icon weight="bold" className="w-5 h-5 text-[#F08A24]" />
            </div>
            <span className="text-sm font-medium text-gray-700 dark:text-gray-200">{label}</span>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-3">
        <Button
          onClick={onSkip}
          variant="ghost"
          className="text-gray-400 hover:text-gray-600 dark:text-gray-500"
        >
          {t('skipSetup')}
        </Button>
        <Button onClick={onNext} className="bg-[#F08A24] hover:bg-[#e07a14] text-white">
          {t('professional.welcome.cta')}
          <ArrowRight weight="bold" className="ml-2 w-4 h-4" />
        </Button>
      </div>
    </div>
  )
}
